﻿import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSession } from "@/auth/session";
import {
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  type NotificationItem,
} from "@/lib/api";

type NotifCategoria = NotificationItem["category"];

const tipoConfig: Record<string, { icon: string; color: string; bg: string; border: string }> = {
  error: { icon: "ri-error-warning-line", color: "text-red-600", bg: "bg-red-50", border: "border-red-200" },
  warning: { icon: "ri-alert-line", color: "text-amber-600", bg: "bg-amber-50", border: "border-amber-200" },
  success: { icon: "ri-checkbox-circle-line", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200" },
  info: { icon: "ri-information-line", color: "text-teal-600", bg: "bg-teal-50", border: "border-teal-200" },
};

const categoriaLabel: Record<NotifCategoria, string> = {
  corte: "Cortes",
  morosidad: "Morosidad",
  operaciones: "Operaciones",
  facturacion: "Facturación",
  inventario: "Inventario",
  sistema: "Sistema",
};

const categoriaIcon: Record<NotifCategoria, string> = {
  corte: "ri-scissors-cut-line",
  morosidad: "ri-money-dollar-circle-line",
  operaciones: "ri-tools-line",
  facturacion: "ri-file-list-3-line",
  inventario: "ri-store-2-line",
  sistema: "ri-settings-3-line",
};

const CATEGORIAS: NotifCategoria[] = ["corte", "morosidad", "operaciones", "facturacion", "inventario", "sistema"];

export default function NotificacionesPage() {
  const navigate = useNavigate();
  const { session } = useSession();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filtroCategoria, setFiltroCategoria] = useState<NotifCategoria | "todas">("todas");
  const [filtroLeida, setFiltroLeida] = useState<"todas" | "no-leidas" | "leidas">("todas");
  const [busqueda, setBusqueda] = useState("");

  useEffect(() => {
    let cancelled = false;
    if (!session) return;

    async function load(): Promise<void> {
      setLoading(true);
      setError("");
      try {
        const result = await listNotifications(session, 150);
        if (!cancelled) setItems(result.items);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "No se pudieron cargar notificaciones");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [session]);

  const noLeidas = items.filter((n) => !n.read).length;

  const filtradas = useMemo(() => {
    return items.filter((n) => {
      const matchCat = filtroCategoria === "todas" || n.category === filtroCategoria;
      const matchLeida =
        filtroLeida === "todas" ||
        (filtroLeida === "no-leidas" && !n.read) ||
        (filtroLeida === "leidas" && n.read);
      const term = busqueda.trim().toLowerCase();
      const matchBusq = term.length === 0 || n.title.toLowerCase().includes(term) || n.description.toLowerCase().includes(term);
      return matchCat && matchLeida && matchBusq;
    });
  }, [items, filtroCategoria, filtroLeida, busqueda]);

  async function marcarLeida(key: string): Promise<void> {
    if (!session) return;
    await markNotificationRead(session, key);
    setItems((prev) => prev.map((n) => (n.key === key ? { ...n, read: true } : n)));
  }

  async function marcarTodasLeidas(): Promise<void> {
    if (!session) return;
    await markAllNotificationsRead(session);
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
  }

  async function handleClick(notif: NotificationItem): Promise<void> {
    if (!notif.read) await marcarLeida(notif.key);
    if (notif.link) navigate(notif.link);
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Notificaciones</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {noLeidas > 0 ? (
              <span><span className="font-semibold text-gray-700">{noLeidas}</span> sin leer</span>
            ) : (
              "Todas las notificaciones leídas"
            )}
          </p>
        </div>
        {noLeidas > 0 && (
          <button
            onClick={() => void marcarTodasLeidas()}
            className="flex items-center gap-2 px-4 py-2 text-sm text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-check-double-line" />
            Marcar todas como leídas
          </button>
        )}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-4 mb-4 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
            <i className="ri-search-line text-sm" />
          </div>
          <input
            type="text"
            placeholder="Buscar notificación..."
            value={busqueda}
            onChange={(e) => setBusqueda(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:border-teal-400 focus:bg-white transition-colors"
          />
        </div>

        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          {(["todas", "no-leidas", "leidas"] as const).map((opt) => (
            <button
              key={opt}
              onClick={() => setFiltroLeida(opt)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors cursor-pointer whitespace-nowrap ${
                filtroLeida === opt ? "bg-white text-gray-800 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}
            >
              {opt === "todas" ? "Todas" : opt === "no-leidas" ? "Sin leer" : "Leídas"}
            </button>
          ))}
        </div>

        <select
          value={filtroCategoria}
          onChange={(e) => setFiltroCategoria(e.target.value as NotifCategoria | "todas")}
          className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-gray-50 focus:outline-none focus:border-teal-400 cursor-pointer"
        >
          <option value="todas">Todas las categorías</option>
          {CATEGORIAS.map((cat) => (
            <option key={cat} value={cat}>{categoriaLabel[cat]}</option>
          ))}
        </select>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={() => setFiltroCategoria("todas")}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
            filtroCategoria === "todas"
              ? "bg-gray-800 text-white"
              : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
          }`}
        >
          Todas
        </button>
        {CATEGORIAS.map((cat) => {
          const count = items.filter((n) => n.category === cat && !n.read).length;
          return (
            <button
              key={cat}
              onClick={() => setFiltroCategoria(cat)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                filtroCategoria === cat
                  ? "bg-gray-800 text-white"
                  : "bg-white border border-gray-200 text-gray-600 hover:border-gray-300"
              }`}
            >
              <i className={`${categoriaIcon[cat]} text-xs`} />
              {categoriaLabel[cat]}
              {count > 0 && (
                <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-xs font-bold ${
                  filtroCategoria === cat ? "bg-white/20 text-white" : "bg-red-100 text-red-600"
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="bg-white rounded-xl border border-gray-100 p-8 text-sm text-gray-500">Cargando notificaciones...</div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">{error}</div>
      ) : filtradas.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
            <i className="ri-notification-off-line text-xl text-gray-400" />
          </div>
          <p className="text-gray-500 text-sm">No hay notificaciones que coincidan con los filtros</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtradas.map((notif) => {
            const cfg = tipoConfig[notif.type];
            return (
              <div
                key={notif.key}
                className={`group bg-white rounded-xl border transition-all duration-150 ${
                  !notif.read
                    ? "border-l-4 border-l-teal-500 border-gray-100 shadow-sm"
                    : "border-gray-100 hover:border-gray-200"
                }`}
              >
                <div className="flex items-start gap-4 p-4">
                  <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${cfg.bg}`}>
                    <i className={`${cfg.icon} text-base ${cfg.color}`} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-sm font-semibold ${notif.read ? "text-gray-700" : "text-gray-900"}`}>
                          {notif.title}
                        </span>
                        {!notif.read && <span className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0" />}
                        <span className={`text-xs px-2 py-0.5 rounded-full border ${cfg.bg} ${cfg.color} ${cfg.border}`}>
                          {categoriaLabel[notif.category]}
                        </span>
                      </div>
                      <span className="text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{toRelativeTime(notif.createdAt)}</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-1 leading-relaxed">{notif.description}</p>

                    <div className="flex items-center gap-3 mt-3">
                      {notif.link && (
                        <button
                          onClick={() => void handleClick(notif)}
                          className="flex items-center gap-1.5 text-xs text-teal-700 font-medium hover:text-teal-800 cursor-pointer transition-colors"
                        >
                          <i className="ri-arrow-right-circle-line" />
                          Ver detalle
                        </button>
                      )}
                      {!notif.read && (
                        <button
                          onClick={() => void marcarLeida(notif.key)}
                          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 cursor-pointer transition-colors"
                        >
                          <i className="ri-check-line" />
                          Marcar como leída
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filtradas.length > 0 && !loading && !error && (
        <p className="text-center text-xs text-gray-400 mt-6">
          Mostrando {filtradas.length} de {items.length} notificaciones
        </p>
      )}
    </div>
  );
}

function toRelativeTime(isoDate: string): string {
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return "";
  const diffMs = Date.now() - date.getTime();
  const diffMin = Math.max(1, Math.round(diffMs / 60000));
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffHour = Math.round(diffMin / 60);
  if (diffHour < 24) return `Hace ${diffHour} h`;
  const diffDay = Math.round(diffHour / 24);
  return `Hace ${diffDay} d`;
}
